import React from 'react';
import { Link } from 'react-router-dom';
import { ETSY_SHOP_URL, CONTACT_EMAIL } from '../lib/constants';

interface NavLinkProps {
  to: string;
  children: React.ReactNode;
}

const NavLink: React.FC<NavLinkProps> = ({ to, children }) => (
  <Link
    to={to}
    className="inline-flex items-center px-1 pt-1 text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors"
  >
    {children}
  </Link>
);

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-8">
              <Link 
                to="/" 
                className="text-2xl font-bold text-green-700 tracking-tight"
              >
                Megadish
              </Link>
              <div className="hidden md:flex space-x-8">
                <NavLink to="/">Home</NavLink>
                <NavLink to="/protect">Protect</NavLink>
                <NavLink to="/share">Share</NavLink>
                <NavLink to="/extend">Extend</NavLink>
                <NavLink to="/multiply">Multiply</NavLink>
                <NavLink to="/privacy">Privacy</NavLink>
              </div>
            </div>
            <a 
              href={ETSY_SHOP_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
            >
              Shop on Etsy
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto py-12 px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <p className="text-slate-400">Email: {CONTACT_EMAIL}</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li>
                  <a 
                    href={ETSY_SHOP_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    Shop on Etsy
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">The Four Pillars</h3>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/protect" className="hover:text-white transition-colors">🔒 Protect</Link></li>
                <li><Link to="/share" className="hover:text-white transition-colors">👨‍👩‍👧‍👦 Share</Link></li>
                <li><Link to="/extend" className="hover:text-white transition-colors">📡 Extend</Link></li>
                <li><Link to="/multiply" className="hover:text-white transition-colors">➕ Multiply</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
            <p>&copy; {currentYear} Megadish. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
